import { parseDate } from '@internationalized/date';
import { Spacing } from '@persona-id/design-tokens';
import { cloneDeep } from 'lodash';
import React, { ChangeEvent, MouseEvent, useState } from 'react';
import { Controller, Field } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import Divider from 'dashboard/components/Divider';
import FileInputField from 'dashboard/forms/FormUpdateAccountNew/FileInputField';
import HashFieldComponent from 'dashboard/forms/FormUpdateAccountNew/HashFieldComponent';
import MultiChoicesField from 'dashboard/forms/FormUpdateAccountNew/MultiChoicesField';
import Form from 'dashboard/layouts/FormLayout/Form';
import TableLayout, { TableTH } from 'dashboard/layouts/TableLayout';
import { TableNullState } from 'dashboard/layouts/TableLayoutV2';
import { AccountState } from 'dashboard/redux/slices/account';
import { Z_INDICES } from 'dashboard/styles/variables';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card/Card';
import Checkbox from 'design-system/components/Checkbox/Checkbox';
import DateField from 'design-system/components/DatePicker/DateField';
import Input from 'design-system/components/Input';
import Select, { Option } from 'design-system/components/Select';
import TableCell from 'design-system/components/Table/TableCell';
import defaultTheme from 'design-system/theme';
import {
  FieldBoolean,
  FieldChoices,
  FieldDate,
  FieldDatetime,
  FieldFile,
  FieldInteger,
  FieldMultiChoices,
  FieldNumber,
  FieldSchema,
  FieldSchemaChoices,
  FieldSchemaHash,
  FieldSchemaMultiChoices,
  FieldsMap,
  FieldString,
  FieldType,
} from 'lib/fields';
import { ApiModel, ApiRelationshipIdentifier } from 'lib/interfaces/api';
import { useFormSecurely } from 'lib/security/reactHookForm';

const StyledTableLayout = styled(TableLayout)`
  table-layout: fixed;
  width: 100%;
`;

const EqualWidthTableTH = styled(TableTH)`
  width: 50%;
`;

const StyledDivider = styled(Divider)`
  margin-top: 0;
  margin-bottom: 0;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  gap: ${Spacing[2]};
  padding: ${Spacing[3]} ${Spacing[4]};
`;

const TableCellForLinks = styled(TableCell)`
  line-height: ${({ theme }) => theme.text.paragraph.sm.lineHeight};

  & > a {
    display: block;
  }
`;

const StyledLink = styled(Link)`
  text-decoration: underline;
  margin-right: ${Spacing[2]};
  white-space: nowrap;
`;

const RelationLabelCell = styled(TableCell)`
  color: ${defaultTheme.color.text.secondary};
`;

export const transformKeyToLabel = (key: string): string => {
  return key
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const getFieldPlaceholder = (type: FieldType): string => {
  switch (type) {
    case FieldType.Choices:
      return 'Select an option';
    case FieldType.String:
      return 'e.g. abc123';
    case FieldType.Integer:
      return 'e.g. 123';
    case FieldType.Number:
      return 'e.g. 123.4';
    default:
      return '';
  }
};

const getFieldType = (fieldType: FieldType): string => {
  switch (fieldType) {
    case FieldType.Boolean:
      return 'checkbox';
    case FieldType.Integer:
    case FieldType.Number:
      return 'number';
    case FieldType.String:
      return 'text';
    default:
      return 'text';
  }
};

const getComponentStep = (fieldType: FieldType): string => {
  switch (fieldType) {
    case FieldType.Integer:
      return '1';
    case FieldType.Number:
      return 'any';
    default:
      return '';
  }
};
export const renderFieldComponent = (
  value: ValueType | Record<string, Field> | any,
  fieldType: FieldType,
  onChange: ((value: ValueType) => void) | ((path: string[], value: ValueType) => void),
  onBlur: () => void,
  isDisabled: boolean,
  accountSchema: FieldSchema | FieldSchemaChoices | FieldSchemaMultiChoices | undefined,
  hashPath: string[] = [],
  fieldKey: string,
) => {
  const handleChange = (newValue: ValueType) => {
    if (typeof onChange === 'function') {
      if (hashPath.length > 0) {
        (onChange as (path: string[], value: ValueType) => void)(hashPath, newValue);
      } else {
        (onChange as (value: ValueType) => void)(newValue);
      }
    }
  };

  switch (fieldType) {
    case FieldType.File:
      return (
        <FileInputField
          value={value}
          onChange={handleChange}
          isDisabled={isDisabled}
          fieldKey={fieldKey}
        />
      );
    case FieldType.Choices:
      if (accountSchema?.type === 'choices' && Array.isArray(accountSchema.config.options)) {
        return (
          <Select
            portal={true}
            value={value ?? ''}
            onValueChange={handleChange}
            placeholder={getFieldPlaceholder(fieldType)}
            contentProps={{ style: { zIndex: Z_INDICES.modalDropdown } }}
            disabled={isDisabled}
          >
            {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
            <Option value={null}>No choice</Option>
            {accountSchema.config.options.map((option: string, index: number) => (
              <Option key={index} value={option}>
                {option}
              </Option>
            ))}
          </Select>
        );
      }
      return <></>;
    case FieldType.MultiChoices:
      return (
        <MultiChoicesField
          accountSchema={accountSchema as FieldSchemaMultiChoices}
          value={value}
          onChange={handleChange}
          isDisabled={isDisabled}
        />
      );
    case FieldType.Date:
    case FieldType.Datetime: {
      return (
        <DateField
          onChange={(nextDate) => {
            handleChange(nextDate?.toString() ?? null);
          }}
          value={value ? parseDate(value as string) : undefined}
          onBlur={onBlur}
          disabled={isDisabled}
          shouldForceLeadingZeros
        />
      );
    }
    case FieldType.Boolean:
      return (
        <Checkbox
          checked={value}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(e.target.checked)}
          disabled={isDisabled}
        />
      );
    case FieldType.Integer:
    case FieldType.Number:
    case FieldType.String:
      return (
        <Input
          value={value}
          type={getFieldType(fieldType)}
          placeholder={getFieldPlaceholder(fieldType)}
          disabled={isDisabled}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            handleChange(e.target.value);
          }}
          step={getComponentStep(fieldType)}
        />
      );
    default:
      return <></>;
  }
};

const FormUpdateAccountNew = ({
  fields,
  accountSchemas,
  relations,
  onSubmit,
  hideModal,
}: FormUpdateAccountNewProps) => {
  // React Hook Form has issues with circular dependencies when handling deeply nested fields, such as hash fields, arrays etc.
  // It perceives these fields as potentially infinitely deep. To avoid these issues, we separate hash fields and non-hash fields.
  // Non-hash fields are managed by React Hook Form using the hook useFormSecurely.
  // Hash fields are managed separately using local state with useState.
  // This separation prevents React Hook Form from encountering circular dependency errors during validation and updates.
  const nonHashFields = Object.keys(fields).reduce((acc: any, key) => {
    if (fields[key].type !== FieldType.Hash) {
      acc[key] = fields[key];
    }
    return acc;
  }, {});

  const hashFields = Object.keys(fields).reduce((acc: any, key) => {
    if (fields[key].type === FieldType.Hash) {
      acc[key] = fields[key];
    }
    return acc;
  }, {});

  const {
    handleSubmit,
    control,
    formState: { isSubmitting, isValid },
  } = useFormSecurely<SupportedFieldsMap>({
    defaultValues: nonHashFields,
    mode: 'onChange',
  });

  const [hashFieldsState, setHashFieldsState] = useState(cloneDeep(hashFields));

  const handleHashFieldChange = (hashPath: string[], value: ValueType) => {
    setHashFieldsState((prev: any) => {
      const newState = { ...prev };
      let current = newState;
      for (let i = 0; i < hashPath.length - 1; i++) {
        if (!current[hashPath[i]]) {
          current[hashPath[i]] = {};
        }
        current = current[hashPath[i]].value;
      }
      const lastKey = hashPath[hashPath.length - 1];
      if (current[lastKey]) {
        current[lastKey] = {
          ...current[lastKey],
          value: value,
        };
      } else {
        current[lastKey] = { value: value, type: typeof value };
      }
      return newState;
    });
  };

  const handleFormSubmit = async (data: FieldsMap) => {
    const mergedData = {
      ...data,
      ...hashFieldsState,
    };
    await onSubmit(mergedData);
  };

  const navigate = useNavigate();

  const handleLinkClick = (event: MouseEvent<HTMLAnchorElement>, to: string) => {
    event.preventDefault();
    hideModal();
    setTimeout(() => {
      navigate(to);
    }, 0);
  };

  return (
    <Form onSubmit={handleSubmit(handleFormSubmit)}>
      <Card>
        <Card.Body>
          <StyledTableLayout
            dataTest='fields-table'
            isEmpty={Object.keys(fields).length === 0}
            isLoading={false}
            showHeader={Object.keys(fields).length !== 0}
            headerTR={
              <>
                <EqualWidthTableTH>Field</EqualWidthTableTH>
                <EqualWidthTableTH>Value</EqualWidthTableTH>
              </>
            }
            nullState={
              <TableNullState dataTestBlock='fields-table'>
                Looks like there aren&#39;t any fields for this account yet.
              </TableNullState>
            }
          >
            <tbody>
              {Object.keys(fields).map((key) => {
                const inputField = fields[key];
                const schema = accountSchemas.find((schema: FieldSchema) => schema.key === key);

                if (inputField.type !== FieldType.Hash) {
                  return (
                    <tr key={key}>
                      <TableCell size='md'>{schema?.label || transformKeyToLabel(key)}</TableCell>
                      <TableCell size='md'>
                        <Controller
                          name={`${key}.value`}
                          control={control}
                          render={({ field }) =>
                            renderFieldComponent(
                              field.value,
                              inputField.type,
                              field.onChange,
                              field.onBlur,
                              isSubmitting,
                              schema,
                              [],
                              key,
                            )
                          }
                        />
                      </TableCell>
                    </tr>
                  );
                } else {
                  return (
                    <HashFieldComponent
                      key={key}
                      field={hashFieldsState[key]}
                      hashSchema={schema as FieldSchemaHash}
                      path={[key]}
                      onChange={handleHashFieldChange}
                      isDisabled={isSubmitting}
                      level={0}
                    />
                  );
                }
              })}
              {Object.entries(relations ?? {}).map(([key, objects]) => (
                <tr key={key}>
                  <RelationLabelCell>{transformKeyToLabel(key).toLowerCase()}</RelationLabelCell>
                  <TableCellForLinks>
                    {objects.data != null &&
                      (Array.isArray(objects.data) ? (
                        <>
                          {objects.data.map((o: ApiRelationshipIdentifier<ApiModel>) => (
                            <StyledLink
                              id={`form-update-account-new__relalion_${o.id}`}
                              key={o.id}
                              to={`/dashboard/accounts/${o.id}`}
                              onClick={(e) => handleLinkClick(e, `/dashboard/accounts/${o.id}`)}
                            >
                              {o.id}
                            </StyledLink>
                          ))}
                        </>
                      ) : (
                        <StyledLink
                          id={`form-update-account-new__relalion_${objects.data.id}`}
                          to={`/dashboard/accounts/${objects.data.id}`}
                          onClick={(e) =>
                            handleLinkClick(
                              e,
                              `/dashboard/accounts/${(objects.data as ApiRelationshipIdentifier<ApiModel>).id}`,
                            )
                          }
                        >
                          {objects.data.id}
                        </StyledLink>
                      ))}
                  </TableCellForLinks>
                </tr>
              ))}
            </tbody>
          </StyledTableLayout>
        </Card.Body>
      </Card>
      <StyledDivider full />
      <ButtonGroup>
        <Button
          id='confirmation-new-modal__cancel'
          data-test='confirmation-new-modal__cancel'
          variant='basic'
          disabled={isSubmitting}
          isPending={isSubmitting}
          onClick={hideModal}
          width='auto'
        >
          Cancel
        </Button>
        <Button
          id='edit-new-account-modal__confirm'
          data-test='edit-new-account-modal__confirm'
          type='submit'
          disabled={isSubmitting || !isValid}
          isPending={isSubmitting}
          width='auto'
        >
          Confirm
        </Button>
      </ButtonGroup>
    </Form>
  );
};

export type FileType = {
  filename: string;
  data: string;
  'mime-type': string;
  'byte-size': number;
};

export type ValueType = string | string[] | number | boolean | FileType | null;

export type SupportedFieldsMap = Record<
  string,
  | FieldBoolean
  | FieldChoices
  | FieldDate
  | FieldDatetime
  | FieldInteger
  | FieldNumber
  | FieldMultiChoices
  | FieldString
  | FieldFile
>;

export interface FormUpdateAccountNewProps {
  fields: FieldsMap;
  accountSchemas: FieldSchema[];
  relations: AccountState['relations'];
  onSubmit: (values: SupportedFieldsMap) => Promise<void>;
  hideModal: () => void;
}

export interface FormUpdateAccountNewData {
  fields?: FieldsMap;
}

export default FormUpdateAccountNew;
